import { memo, useMemo } from 'react';
import { LESSONS_PAGE } from 'constants/pages';
import { SportsEnum } from 'types/generated/client';
import { type MarketplaceLinkParams, marketplaceUrl } from 'utils/shared/url/marketplaceUrl';
import useNearSport from 'hooks/useNearSport';
import ArrowRight from 'svg/ArrowRight';
import type { FindFilters } from 'screens/Find/filters';
import { FIND_RADIUS_MILES } from 'screens/Find/geo';
import { HomeFilters } from 'screens/PlayerHomeV3/components/GetOnCourtSection/types';
import { chosenRadiusMiles } from 'screens/PlayerHomeV3/components/TopOpportunitiesSection/sheetFilters';
import { ButtonLink } from 'components/Button';
import CardsSection from 'components/CardSection';
import ExploreLessonCard from 'components/Explore/Lessons/ExploreLessonCard';
import classNames from 'styles/utils/classNames';

const CARD_COUNT = 4;
const LESSON_ICON = '/images/player-home-v2/search-bar-icons/lesson-gradient.png';

const MemoizedExploreLessonCard = memo(ExploreLessonCard);

interface Props {
  filters: HomeFilters;
  sheetFilters?: FindFilters;
  marketplaceParams?: MarketplaceLinkParams;
}

export default function ImproveYourGameSection({
  filters,
  sheetFilters,
  marketplaceParams,
}: Props) {
  // Location comes from the geo context, which the bar's AddressBar writes to.
  const sports = useMemo(() => [filters.sport], [filters.sport]);
  const { decoratedCoaches, loading } = useNearSport({
    sports,
    radiusMiles: sheetFilters ? chosenRadiusMiles(sheetFilters) : FIND_RADIUS_MILES,
  });
  const coaches = decoratedCoaches.slice(0, CARD_COUNT);

  const noData = !loading && coaches.length === 0;

  return (
    // @todo empty-state design pending
    <section
      className={classNames(
        'container mx-auto mt-[0.875rem] max-lg:pr-0 max-sm:-mt-ds-2xl sm:max-lg:-mt-12',
        noData && 'hidden',
      )}
    >
      <div className="my-ds-2xl h-px w-full bg-color-border-input-lightmode dark:bg-color-border-input-darkmode max-lg:mr-ds-lg lg:hidden" />
      <div className="flex items-center gap-ds-sm max-lg:pr-ds-lg">
        <img src={LESSON_ICON} alt="" className="h-12 w-12 shrink-0 max-lg:hidden" />
        <h2 className="max-lg:typography-informative-subheading-medium-highlight lg:typography-product-heading-desktop text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
          Improve your game
        </h2>
      </div>

      <div className="mt-ds-lg">
        <CardsSection
          loading={loading}
          gap="1rem"
          scrollOffset={{ base: 180, sm: 0 }}
          visibleCards={{ base: 1, sm: 2, md: 3, lg: 4 }}
          cardsWrapperClassName="-my-3 py-3"
          data={coaches}
          renderCard={(coach) => (
            <MemoizedExploreLessonCard
              key={coach.id}
              id={coach.id}
              slug={coach.username}
              photoUrl={coach.photoUrl}
              certificationImages={[]}
              reviews={coach.reviews}
              defaultSport={coach.defaultSport}
              coachSports={coach.coachSports}
              fullName={coach.fullName}
              price={coach.price}
              lessonGiven={coach.lessonGiven}
              distance={coach.distance}
              isProfessionalRacquetRegistryActive={coach.isProfessionalRacquetRegistryActive}
              currency={coach.currency}
              activeSport={(coach.defaultSport as SportsEnum) || SportsEnum.Pickleball}
            />
          )}
        />
      </div>

      <div className="mt-ds-xl flex justify-end max-lg:mt-ds-lg max-lg:pr-ds-lg">
        <ButtonLink
          href={marketplaceUrl(LESSONS_PAGE, marketplaceParams)}
          variant="primary-light"
          size="md"
          isInline
          iconLeft={<img src={LESSON_ICON} alt="" className="h-4 w-4 shrink-0" />}
          iconRight={<ArrowRight className="h-4 w-4 shrink-0" />}
          className="shadow-[0_4px_20px_0_rgba(0,0,0,0.16)]"
        >
          View all coaches
        </ButtonLink>
      </div>
    </section>
  );
}
